
import type { FacetsDTO } from '@empathyco/playboard-core-domain'
import type { PropType } from 'vue'

import type { BrowseFacetDTO, RegularFilter } from '../../models'
import type { EpbSelectOption } from '../shared/epb-select'
import { computed, defineComponent, onMounted, ref, watch } from 'vue'

import { useFacets } from '../../composables'
import { FacetTypeEnum } from '../../enums'
import { UiConfirm } from '../keen-ui'
import { EpbSelect } from '../shared/epb-select'

export default defineComponent({
  name: 'EpbFacetsModal',
  components: { EpbSelect, UiConfirm },
  props: {
    item: {
      type: Object as PropType<FacetsDTO>,
      required: true,
    },
    pivots: {
      type: Object as PropType<Record<string, RegularFilter>>,
      required: true,
    },
    messages: {
      type: Object,
      required: true,
    },
  },
  setup(props) {
    const baseClass = 'epb-facets-modal'

    const { facetsOptions, searchFacets } = useFacets()

    const uiConfirm = ref()

    const facetsOptionsSelected = ref<Array<BrowseFacetDTO>>([])

    const optionsSelected = ref<Array<string>>([])

    const open = (): void => {
      uiConfirm.value?.open()
    }

    const removeSelectedOptions = (): void => {
      facetsOptionsSelected.value = facetsOptionsSelected.value.filter(option => {
        if (option.type === FacetTypeEnum.HIERARCHICAL) {
          return props.item.config.facetableFields.hierarchicalFacets?.some(
            item => item.field === option.label,
          )
        }
        if (option.type === FacetTypeEnum.VALUE) {
          return props.item.config.facetableFields.valueFacets?.some(
            item => item.field === option.label,
          )
        }
        if (option.type === FacetTypeEnum.RANGE) {
          return props.item.config.facetableFields.rangeFacets?.some(
            item => item.field === option.label,
          )
        }
        if (option.type === FacetTypeEnum.BOOLEAN) {
          return props.item.config.facetableFields.booleanFacets?.some(
            item => item.field === option.label,
          )
        }
        if (option.type === FacetTypeEnum.SLIDER) {
          return props.item.config.facetableFields.sliderFacets?.some(
            item => item.field === option.label,
          )
        }
        return false
      })

      optionsSelected.value = facetsOptionsSelected.value.map(item => item.label)
    }

    onMounted(async () => {
      await searchFacets(props.item, props.pivots)
      facetsOptions.value.forEach(option => {
        if (option.type === FacetTypeEnum.HIERARCHICAL) {
          props.item.config.facetableFields.hierarchicalFacets?.forEach(hierarchicalFacetItem => {
            if (option.label === hierarchicalFacetItem.field) {
              facetsOptionsSelected.value.push(option)
            }
          })
        } else if (option.type === FacetTypeEnum.VALUE) {
          props.item.config.facetableFields.valueFacets?.forEach(valueFacetItem => {
            if (option.label === valueFacetItem.field) {
              facetsOptionsSelected.value.push(option)
            }
          })
        } else if (option.type === FacetTypeEnum.RANGE) {
          props.item.config.facetableFields.rangeFacets?.forEach(rangeFacetItem => {
            if (option.label === rangeFacetItem.field) {
              facetsOptionsSelected.value.push(option)
            }
          })
        } else if (option.type === FacetTypeEnum.BOOLEAN) {
          props.item.config.facetableFields.booleanFacets?.forEach(booleanFacetItem => {
            if (option.label === booleanFacetItem.field) {
              facetsOptionsSelected.value.push(option)
            }
          })
        } else if (option.type === FacetTypeEnum.SLIDER) {
          props.item.config.facetableFields.sliderFacets?.forEach(sliderFacetItem => {
            if (option.label === sliderFacetItem.field) {
              facetsOptionsSelected.value.push(option)
            }
          })
        }
      })
      optionsSelected.value = facetsOptionsSelected.value.map(item => item.label)
    })

    watch(
      props.item,
      async () => {
        await searchFacets(props.item, props.pivots)
        removeSelectedOptions()
      },
      { immediate: true },
    )

    const updateFacetsOptionsSelected = (selectedValues: string[]): void => {
      facetsOptionsSelected.value = facetsOptions.value.filter(option =>
        selectedValues.includes(option.label),
      )
    }

    const handleInput = (value: string[]): void => {
      optionsSelected.value = value
      updateFacetsOptionsSelected(value)
    }

    const options = computed((): EpbSelectOption[] => {
      return facetsOptions.value
        ? facetsOptions.value.map((facet: BrowseFacetDTO) => {
            return {
              label: facet.label,
              value: facet.label || facet.id,
            }
          })
        : []
    })

    watch(
      () => props.pivots,
      async () => {
        await searchFacets(props.item, props.pivots)
      },
      { deep: true },
    )

    return {
      baseClass,
      uiConfirm,
      facetsOptionsSelected,
      facetsOptions,
      open,
      options,
      optionsSelected,
      handleInput,
    }
  },
})
